import _ from "lodash";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { createUseStyles } from "react-jss";
import { InlineIcon } from "@iconify/react";
import { Formik, Form, Field } from "formik";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { useContext } from "react";
import { cStyler } from "../../../helpers";
import jssVariables from "../../../jss_setup";
import { modalGenericStyles } from "../../../jss_setup/modals";
import { NoScrollContext } from "../../../context";

const paxPickerStyles = createUseStyles({
  ...modalGenericStyles,
  PaxPickerModal: {
    ...modalGenericStyles.modal,
  },
  card: {
    ...modalGenericStyles.card,
    width: "90vw",
    padding: `calc(${jssVariables.normal_gap} * 2)`,
  },
  body: modalGenericStyles.cardBody,
  actions: modalGenericStyles.cardActions,
  paxGroup: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    placeItems: "center",
    placeContent: "center",
    fontSize: "large",
    padding: [`calc(${jssVariables.normal_gap} / 2)`, jssVariables.normal_gap],
    border: `2px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.paxGroup", {}),
  }),
  paxBtn: (props) => ({
    borderRadius: "50%",
    height: "2.5rem",
    width: "2.5rem",
    fontSize: "larger",
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    background: "none",
    border: `2px solid ${jssVariables.colors.base_color}`,
    ..._.get(props, "customStyles.paxBtn", {}),
  }),
  paxPlusBtn: (props) => ({
    background: jssVariables.colors.base_color,
    color: "white",
    ..._.get(props, "customStyles.paxPlusBtn", {}),
  }),
  ages: {
    display: "grid",
    gridGap: jssVariables.normal_gap,
    fontSize: "large",
    maxHeight: "18rem",
    overflow: "auto",
  },
  ageItem: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    alignItems: "center",
    textAlign: "left",
    gridAutoRows: "max-content",
    color: jssVariables.colors.base_color,
    "& select": {
      padding: jssVariables.normal_gap,
      border: `2px solid ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.ageItem", {}),
  }),
  applyBtn: (props) => ({
    padding: jssVariables.normal_gap,
    background: jssVariables.colors.base_color,
    color: "white",
    fontWeight: "bold",
    fontSize: "large",
    ..._.get(props, "customStyles.applyBtn", {}),
  }),
  cancelBtn: (props) => ({
    padding: jssVariables.normal_gap,
    background: "none",
    color: jssVariables.colors.base_color,
    border: `2px solid ${jssVariables.colors.base_color}`,
    fontWeight: "bold",
    fontSize: "large",
    ..._.get(props, "customStyles.cancelBtn", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    card: { width: "30vw" },
  },
});
export const PaxPickerModal = ({
  adults,
  seniors,
  childs,
  children_ages,
  infantsOnLap,
  infantsInSeat,
  onFormUpdate,
  onCancel,
}) => {
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { t } = useContext(TranslationContext);
  const { type, brand } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "paxModalCustomStyles",
  });
  const classes = paxPickerStyles({ customStyles });

  useEffect(() => {
    toggleNoScroll(true);
    return () => toggleNoScroll(false);
  }, []);

  return (
    <div className={classes.PaxPickerModal}>
      <div className={classes.card}>
        <Formik
          initialValues={{
            adults,
            seniors,
            children: childs,
            children_ages,
            infantsOnLap,
            infantsInSeat,
          }}
          onSubmit={(values) => {
            const form = _.cloneDeep(values);
            form.children_ages = form.children_ages.map((age) => parseInt(age));
            onFormUpdate({ form });
            onCancel();
          }}>
          {({ values, setFieldValue }) => (
            <Form>
              <div className={classes.body}>
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.adults > 1) {
                        setFieldValue("adults", values.adults - 1);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {t("adults")}: {values.adults}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.adults < 9) {
                        setFieldValue("adults", values.adults + 1);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.children > 0) {
                        const ages = [...values.children_ages];
                        ages.pop();
                        setFieldValue("children", values.children - 1);
                        setFieldValue("children_ages", ages);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {t("children")}: {values.children}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.children < 9) {
                        setFieldValue("children", values.children + 1);
                        setFieldValue("children_ages", [
                          ...values.children_ages,
                          "2",
                        ]);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.ages}>
                  {values.children_ages.map((c, idx) => (
                    <div key={idx} className={classes.ageItem}>
                      <strong>
                        {t("child")} {idx + 1} {t("age")} at checkout:
                      </strong>
                      <Field name={`children_ages.${idx}`} as="select">
                        {new Array(18).fill(1).map((age, aidx) => (
                          <option value={aidx} key={aidx}>
                            {aidx} years old.
                          </option>
                        ))}
                      </Field>
                    </div>
                  ))}
                </div>
              </div>
              <div className={classes.actions}>
                <button
                  id="cancelBtn"
                  className={classes.cancelBtn}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onCancel();
                  }}>
                  {t("cancel")}
                </button>
                <button
                  className={classes.applyBtn}
                  id="applyBtn"
                  type="submit">
                  {t("apply")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
PaxPickerModal.defaultProps = {
  customStyles: {},
  adults: 2,
  seniors: 0,
  childs: 0,
  children_ages: [],
};
PaxPickerModal.propTypes = {
  adults: PropTypes.number.isRequired,
  seniors: PropTypes.number,
  childs: PropTypes.number.isRequired,
  children_ages: PropTypes.array.isRequired,
  onFormUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
export default PaxPickerModal;
